import { useState } from "react";
import { Skeleton, Stack, TextField, TextFieldProps } from "@mui/material";
import { Controller, FieldValues, Path, RegisterOptions, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormFieldHint from "./FormFieldHint";

interface Props<T extends FieldValues> extends Omit<TextFieldProps, "name" | "label" | "control"> {
	name: Path<T>;
	label: string;
	readOnly?: boolean;
	isLoading?: boolean;
	hintMessage?: string;
	form: UseFormReturn<T>;
	afterChange?: () => void;
	minFractionDigits?: number;
	maxFractionDigits?: number;
	rules?: Omit<RegisterOptions<T, Path<T>>, "disabled" | "valueAsNumber" | "valueAsDate" | "setValueAs">;
}

/**
 * FormNumericField acts as a wrapper component for numeric only text fields used
 * in conjunction with React Hook Form.
 *
 * @param name              the name of the field bound to this text field
 * @param label             the label of the text field
 * @param readOnly          if true, makes the field read-only
 * @param isLoading         if true, the component will render a skeleton element
 * @param form              the form reference
 * @param afterChange       if defined, this callback is invoked right after internal onChange event
 * @param minFractionDigits the minimum number of fractional digits, defaults to 0
 * @param maxFractionDigits the maximum number of fractional digits, defaults to 0
 * @param hintMessage       the optional hint message that may be rendered at the end of the field
 * @param rules             optional validation rules (if undefined, the field will be optional)
 * @param rest              inherited props
 * @returns                 a TextField wrapped in a React Hook Form Control component
 */
const FormNumericField = <T extends FieldValues>({
	form,
	name,
	label,
	rules,
	readOnly,
	isLoading,
	hintMessage,
	afterChange,
	minFractionDigits = 0,
	maxFractionDigits = 0,
	...rest
}: Props<T>): React.ReactElement => {
	const { t, i18n } = useTranslation("format.numbers");
	const [displayVal, setDisplayVal] = useState<string>("");

	// Get decimal and thousands separators.
	const numberFormatter = new Intl.NumberFormat(i18n.language);
	const numericParts = numberFormatter.formatToParts(1234.56);

	const decimalSeparator = numericParts.find((p) => p.type === "decimal")?.value || ".";
	const thousandsSeparator = numericParts.find((p) => p.type === "group")?.value || ",";

	// Show "*" right after mandatory fields (i.e. those to which validation rules are applied).
	label = rules?.required ? `${label} *` : label;

	const formatNumber = (value: number | string) => {
		if (!value) {
			return "";
		}

		return t("float", {
			val: Number(value),
			minimumFractionDigits: minFractionDigits,
			maximumFractionDigits: maxFractionDigits,
		});
	};

	const parseNumber = (val: string) => {
		// Normalize the numeric value expressed as a string by replacing
		// decimal separator with dot and removing thousands separator.
		const normalizedValue = val
			.replace(new RegExp(`\\${thousandsSeparator}`, "g"), "")
			.replace(decimalSeparator, ".");

		return Number(normalizedValue);
	};

	const valueDidChange = (value: string, onChange: (...event: any[]) => void) => {
		form.clearErrors(name);
		setDisplayVal(value);

		const parsed = parseNumber(value);
		if (Number.isNaN(parsed)) {
			form.setError(name, {
				message: t("this field requires a numeric value", { ns: "common" }),
			});

			afterChange?.();
			return;
		}

		onChange(value);
		afterChange?.();
		form.setValue(name, parsed as any);
		form.trigger(name);
	};

	const didEndEditing = () => {
		const parsed = parseNumber(displayVal);
		const formatted = formatNumber(parsed);
		setDisplayVal(formatted);
	};

	if (isLoading) {
		return (
			<Stack spacing={1} direction="row">
				<Skeleton className="loading-field" />
			</Stack>
		);
	}

	return (
		<Controller
			name={name}
			control={form.control}
			rules={rules}
			render={({ field: { onChange, value }, fieldState: { error } }) => (
				<Stack spacing={1} direction="row">
					<TextField
						{...rest}
						onBlur={didEndEditing}
						value={displayVal !== "" ? displayVal : formatNumber(value)}
						label={label}
						slotProps={{ input: { readOnly } }}
						className={readOnly ? "read-only" : undefined}
						onChange={(e) => valueDidChange(e.target.value, onChange)}
						helperText={error?.message ?? <FormFieldHint text={hintMessage} />}
						error={error !== undefined}
					/>
				</Stack>
			)}
		/>
	);
};

export default FormNumericField;
